import React from 'react';
import ClampedDesc from './ClampedDesc';
import { CardBrands } from './PayBrands';

const cfg = (typeof window !== 'undefined' && window.CHECKOUT_NEXT) || {};

// Fallback icons by gateway type when the gateway provides no logo.
const IconCard = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
);
const IconCash = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /></svg>
);
const IconBank = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 10l9-5 9 5M4 10v8m4-8v8m8-8v8m4-8v8M3 21h18" /></svg>
);
const IconWallet = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M16 12h3" /><path d="M3 8V6a2 2 0 012-2h11" /></svg>
);
const IconStore = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 9l1.5-5h15L21 9M4 9v11h16V9M4 9h16" /></svg>
);
const IconApple = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.36 12.9c-.02-2.06 1.68-3.05 1.76-3.1-.96-1.4-2.45-1.6-2.98-1.62-1.27-.13-2.48.75-3.12.75-.64 0-1.64-.73-2.7-.71-1.39.02-2.67.81-3.38 2.05-1.44 2.5-.37 6.2 1.03 8.23.69.99 1.51 2.1 2.58 2.06 1.04-.04 1.43-.67 2.69-.67 1.25 0 1.61.67 2.7.65 1.12-.02 1.83-1.01 2.51-2.01.79-1.15 1.12-2.27 1.14-2.33-.03-.01-2.18-.84-2.2-3.32zM14.3 6.84c.57-.69.95-1.65.85-2.6-.82.03-1.81.54-2.4 1.23-.53.61-.99 1.59-.87 2.52.91.07 1.85-.46 2.42-1.15z"/></svg>
);
const IconGpay = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M21.6 12.23c0-.64-.06-1.25-.16-1.84H12v3.48h5.38a4.6 4.6 0 0 1-1.99 3.02v2.5h3.22c1.89-1.74 2.99-4.31 2.99-7.16z"/>
    <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.61-2.43l-3.22-2.5c-.9.6-2.05.96-3.39.96-2.6 0-4.8-1.76-5.59-4.13H3.09v2.6A10 10 0 0 0 12 22z"/>
    <path fill="#FBBC04" d="M6.41 13.9a6 6 0 0 1 0-3.8V7.5H3.09a10 10 0 0 0 0 9l3.32-2.6z"/>
    <path fill="#EA4335" d="M12 5.98c1.47 0 2.79.5 3.83 1.5l2.86-2.87C16.95 2.99 14.69 2 12 2A10 10 0 0 0 3.09 7.5l3.32 2.6C7.2 7.74 9.4 5.98 12 5.98z"/>
  </svg>
);

// Device-aware wallet wording: desktop → Apple Pay via QR, iOS → Apple Pay,
// Android → Google Pay (only Apple Pay has the cross-device QR).
export function walletLabel() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const mtp = (typeof navigator !== 'undefined' && navigator.maxTouchPoints) || 0;
  const isIOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && mtp > 1); // iPadOS reports as Mac
  const isAndroid = /Android/.test(ua);
  if (isAndroid) return { title: 'Google Pay', desc: 'Πλήρωσε γρήγορα με Google Pay', brand: 'gpay' };
  if (isIOS) return { title: 'Apple Pay', desc: 'Πλήρωσε γρήγορα με Apple Pay', brand: 'apple' };
  return { title: 'Apple Pay (QR)', desc: 'Σκάναρε τον QR code με το κινητό σου', brand: 'apple' };
}

/* Accepted banks for the direct-transfer method. All badges share the 34×22 box. */
// Piraeus — yellow field, three dark-green slashes.
const BankPiraeus = () => (
  <svg width="34" height="22" viewBox="0 0 34 22" aria-label="Τράπεζα Πειραιώς" role="img">
    <rect width="34" height="22" fill="#FCDD00" />
    <g fill="#0A3B2E">
      <path d="M13 5.5h3.4l-6.6 11H6.4z" /><path d="M18 5.5h3.4l-6.6 11h-3.4z" /><path d="M23 5.5h3.4l-6.6 11h-3.4z" />
    </g>
  </svg>
);
// Alpha — official icon (navy field + white ring + key), merchant-supplied, centred.
const BankAlpha = () => (
  <svg width="34" height="22" viewBox="0 0 34 22" aria-label="Alpha Bank" role="img">
    <rect width="34" height="22" fill="#11366B" />
    <svg x="7" y="1" width="20" height="20" viewBox="0 985.9 528.2 528.2">
      <path fill="#fff" d="M456.6,1251.2c0,137.3-78.6,193.7-193.7,193.7c-113.8,0-192.5-56.3-192.5-193.7s78.6-193.7,193.7-193.7C377.9,1057.5,456.6,1113.8,456.6,1251.2" />
      <path fill="#11366B" d="M99.8,1251.2c0,116.2,59.9,164.3,164.3,164.3s164.3-48.1,164.3-164.3c0-116.2-59.9-164.3-164.3-164.3C158.5,1086.9,99.8,1135,99.8,1251.2" />
      <path fill="#fff" d="M185.4,1122.1v55.2h-52.8c-4.7,15.3-8.2,46.9-9.4,63.4h61v140.8c12.9,4.7,45.8,10.6,61,10.6v-108l92.7,95.1c10.6-3.5,43.4-31.7,46.9-41.1l-91.5-97.4h109.2c-1.2-20-4.7-46.9-10.6-63.4H246.5v-65.7C233.6,1112.7,190.1,1119.7,185.4,1122.1" />
    </svg>
  </svg>
);
// Viva — official mark (white V on Viva dark), merchant-supplied, centred.
const BankViva = () => (
  <svg width="34" height="22" viewBox="0 0 34 22" aria-label="Viva" role="img">
    <rect width="34" height="22" fill="#1E1E2D" />
    <svg x="8" y="4" width="18" height="14" viewBox="0 0 32.0717 24.1141" preserveAspectRatio="xMidYMid meet">
      <path fill="#fff" d="M20.3325 23.6063C18.6281 23.6063 17.0698 22.6291 16.0829 20.9637C15.096 22.6259 13.5377 23.6063 11.8333 23.6063H11.8041C9.94388 23.5933 8.27196 22.4181 7.324 20.4605V20.4475L0 4.86455L9.34004 0.507812L16.0732 14.7825L22.7349 0.507812L32.0717 4.8613L24.8451 20.4638C23.8971 22.4214 22.2252 23.5966 20.365 23.6096H20.3358M18.2321 19.3567C18.7418 20.3729 19.5112 20.954 20.3358 20.954H20.3455C21.1799 20.9475 21.9493 20.3534 22.4492 19.3145L28.5525 6.15015L24.0237 4.03671L17.5503 17.9088L18.2353 19.3567H18.2321ZM9.72312 19.3145C10.2263 20.3534 10.9925 20.9507 11.8236 20.954H11.8366C12.6644 20.954 13.4306 20.3729 13.9435 19.36L14.6155 17.9185L8.06419 4.03346L3.53863 6.14365L9.71988 19.308L9.72312 19.3145Z" />
    </svg>
  </svg>
);
const BankBrands = () => (
  <span className="cn-method__brands">
    <BankPiraeus /><BankAlpha /><BankViva />
  </span>
);

function fallbackIcon(id, title) {
  const s = (id + ' ' + title).toLowerCase();
  // Store-credit wallet (woo-wallet, id `wallet` / «πορτοφόλι») FIRST — otherwise the
  // Apple/Google-Pay rule below catches the word "wallet" and shows an Apple logo.
  if (/πορτοφ/.test(s) || id === 'wallet') return <IconWallet />;
  if (/apple|cn_stripe/.test(s)) return <IconApple />;
  // Card-on-pickup (cop3) before the cash rule — its id contains "cop", but the
  // customer taps a card at the counter, so a banknote icon would misrepresent it.
  if (/κάρτα/.test(s)) return <IconCard />;
  if (/cod|cop|αντικατ|παράδοση/.test(s)) return <IconCash />;
  if (/bacs|τραπεζ|bank|iris/.test(s)) return <IconBank />;
  if (/κατάστημα|store|pickup/.test(s)) return <IconStore />;
  return <IconCard />;
}

// Small info glyph for the unavailable-COD tooltip.
function IconInfo() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function phCapture(event, props) {
  try {
    if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(event, { checkout_flow: 'new', ...props });
    }
  } catch (e) { /* noop */ }
}

// Payment method picker — same card style as the shipping methods.
// `renderUnder(method)` lets a selected row expand extra UI inside its card
// (currently the mobile-verification panel, on αντικαταβολή and on the
// pay-on-collection methods); it sits OUTSIDE the <label> so interacting with it
// doesn't re-toggle the radio.
export default function PaymentMethods({ data: dataProp, value, onChange, renderUnder }) {
  const data = dataProp || cfg.payments || {};
  const methods = Array.isArray(data.methods) ? data.methods : [];
  if (!methods.length) return <p className="cn-hint">Δεν υπάρχουν διαθέσιμοι τρόποι πληρωμής.</p>;

  const firstEnabled = (methods.find((m) => !m.disabled) || {}).id;
  const selectedId = value || data.chosen || firstEnabled;
  const wl = walletLabel();

  return (
    <div className="cn-method-list">
      {methods.map((m) => {
        const selected = !m.disabled && m.id === selectedId;
        const isWallet = m.wallet || m.id === 'cn_stripe_wallet';
        const isCard = /viva|κάρτα|\bcard\b/i.test(`${m.id} ${m.title}`);
        const isBank = /bacs|τραπεζικ|μεταφορ/i.test(`${m.id} ${m.title}`);
        const title = isWallet ? wl.title : m.title;
        const desc = isWallet ? wl.desc : m.desc;
        // Card gateways ship a cramped logo strip — use our clean card icon.
        // Keep a real gateway logo (e.g. PayPal) where it looks good.
        const icon = isWallet
          ? (wl.brand === 'gpay' ? <IconGpay /> : <IconApple />)
          : isCard ? <IconCard />
          : m.icon ? <img src={m.icon} alt="" className="cn-pay__logo" />
          : fallbackIcon(m.id, m.title);
        const isCodUnavailable = m.disabled && m.id === 'cod_disabled';
        return (
          <div
            className={`cn-method-wrap ${selected ? 'is-selected' : ''} ${m.disabled ? 'is-disabled' : ''}`}
            key={m.id}
            // Record a click on the greyed-out COD row + WHY it's blocked, so we can see
            // whether an unavailable αντικαταβολή is a real drop-off driver.
            onClick={isCodUnavailable ? () => phCapture('checkout_cod_unavailable_click', { reason: m.reasonCode || 'other', reason_text: m.desc || '' }) : undefined}
          >
            <label className="cn-method cn-method--pay">
              <input type="radio" name="cn-pay-method" checked={selected} disabled={m.disabled} onChange={() => !m.disabled && onChange(m.id)} />
              <span className="cn-method__text">
                <span className="cn-method__head">
                  <span className="cn-method__icon">{icon}</span>
                  <span className="cn-method__name">{title}</span>
                </span>
                {/* The ⓘ leads the REASON line, not the title — it belongs to the
                    explanation, and inline keeps the sentence wrapping around it. */}
                {desc ? (
                  <ClampedDesc
                    text={desc}
                    lead={isCodUnavailable ? <span className="cn-method__info" aria-hidden="true"><IconInfo /></span> : null}
                  />
                ) : null}
              </span>
              {isCard && <CardBrands />}
              {isBank && <BankBrands />}
            </label>
            {/* Surcharge lives here, not in the title: it only concerns you once
                you've picked the method, and «Αντικαταβολή (+€3.00)» reads like
                part of the method's name. */}
            {selected && m.feeNote ? <div className="cn-method__fee">{m.feeNote}</div> : null}
            {selected && renderUnder ? renderUnder(m) : null}
          </div>
        );
      })}
    </div>
  );
}
